import React, { useState, useCallback, useMemo } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useQuery } from "@apollo/client";
import LoggedHeader from "../../Common/loggedHeader";
import SideMenu from "../../../components/organisms/sidebar";
import DateRangeSelector from "./DateRangeSelector";
import {
  comparisonModeDescription,
  comparisonModeLabel,
  comparisonRange,
  comparisonText,
  mergeComparisonSeries,
} from "./analytics.utils";
import { GET_ORDER_ANALYTICS } from "../../../Graphql/queries";

// Validated for CVD separation and contrast against a light surface before use.
const COLOR_ORDERS = "#FD4E3B";
const COLOR_GMV = "#1976d2";
const COLOR_NET = "#2e7d32";
const AXIS_COLOR = "#8a8a8a";
const GRID_COLOR = "#ececec";
const COMPARE_DASH = "4 4";
const COMPARE_STROKE_OPACITY = 0.55;
const COMPARE_FILL_OPACITY = 0.35;

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const currencyExact = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});
const integer = new Intl.NumberFormat("en-US");
const METRIC_HELP = {
  orders:
    "Completed orders whose payment was actually collected. Orders where the Stripe capture failed are excluded.",
  gmv: "Amount captured from customers, less any refunds. ACH and pre-Stripe orders fall back to the order total.",
  net: "Commission + service fee on the portion we kept, less Stripe's processing fee of 2.9% + $0.30. Stripe keeps its fee on refunds, so it is never prorated.",
};

const sentenceCase = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

// A dashed point is unreadable without its own date, since the x-axis belongs
// to the selected range.
const withComparisonDate = (label, payload) => {
  const compareLabel = payload?.[0]?.payload?.compareLabel;
  return compareLabel ? `${label} · vs ${compareLabel}` : label;
};

function StatTile({ label, value, help, accent, comparison }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "2px",
              backgroundColor: accent,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <MuiTooltip title={help} arrow>
            <InfoOutlinedIcon
              fontSize="inherit"
              sx={{ color: "text.disabled", cursor: "help" }}
            />
          </MuiTooltip>
        </Stack>
        <Typography variant="h4" component="div">
          {value}
        </Typography>
        {comparison && (
          <Typography variant="caption" color="text.secondary">
            {comparison}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          {children}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

const axisProps = {
  stroke: AXIS_COLOR,
  tick: { fill: AXIS_COLOR, fontSize: 12 },
  tickLine: false,
  axisLine: false,
};

export default function Analytics() {
  const [range, setRange] = useState(null);

  // DateRangeSelector fires on mount and on every edit; memoised so it does not
  // re-fire from this component re-rendering with fresh query data.
  const handleRangeChange = useCallback((next) => setRange(next), []);

  const { data, loading, error } = useQuery(GET_ORDER_ANALYTICS, {
    variables: range || {},
    skip: !range,
    fetchPolicy: "cache-and-network",
  });

  const compareMode = range?.compareTo;
  const compareLabel = comparisonModeLabel(compareMode);
  const compareRange = comparisonRange(range, compareMode);
  const {
    data: priorData,
    loading: priorLoading,
    error: priorError,
  } = useQuery(GET_ORDER_ANALYTICS, {
    variables: compareRange || {},
    skip: !compareRange,
    fetchPolicy: "cache-and-network",
  });

  const analytics = data?.getOrderAnalytics;
  // skip drops `data`, so turning the comparison off clears the overlay rather
  // than leaving the previous mode's series on the chart.
  const priorAnalytics = compareRange ? priorData?.getOrderAnalytics : null;
  const series = analytics?.series || [];
  const chartSeries = useMemo(
    () => mergeComparisonSeries(series, priorAnalytics?.series),
    [series, priorAnalytics],
  );
  const showComparison = Boolean(compareLabel && priorAnalytics);

  return (
    <div>
      <LoggedHeader title="Analytics" user="admin" />
      <SideMenu selected="Analytics" user="admin" />
      <div className="contentWrap dashboard" style={{ overflow: "visible" }}>
        <div className="contentWrapInner" style={{ overflow: "visible" }}>
          <Typography variant="h5" component="h1" sx={{ mb: 0.5 }}>
            Order analytics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Completed orders, reported on the date payment was captured
            {analytics?.timeZone ? ` (${analytics.timeZone})` : ""}.
          </Typography>

          <DateRangeSelector onChange={handleRangeChange} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Could not load analytics: {error.message}
            </Alert>
          )}

          {priorError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Current analytics loaded, but the {compareLabel} comparison could
              not be loaded: {priorError.message}
            </Alert>
          )}

          {loading && !analytics && (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          )}

          {analytics && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <StatTile
                    label="Orders"
                    accent={COLOR_ORDERS}
                    value={integer.format(analytics.orderCount)}
                    help={METRIC_HELP.orders}
                    comparison={
                      showComparison
                        ? comparisonText(
                            analytics.orderCount,
                            priorAnalytics.orderCount,
                            compareLabel,
                          )
                        : priorLoading && compareLabel
                        ? `Loading ${compareLabel} comparison...`
                        : null
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StatTile
                    label="GMV"
                    accent={COLOR_GMV}
                    value={currency.format(analytics.gmv)}
                    help={METRIC_HELP.gmv}
                    comparison={
                      showComparison
                        ? comparisonText(
                            analytics.gmv,
                            priorAnalytics.gmv,
                            compareLabel,
                          )
                        : priorLoading && compareLabel
                        ? `Loading ${compareLabel} comparison...`
                        : null
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StatTile
                    label="NET"
                    accent={COLOR_NET}
                    value={currency.format(analytics.net)}
                    help={METRIC_HELP.net}
                    comparison={
                      showComparison
                        ? comparisonText(
                            analytics.net,
                            priorAnalytics.net,
                            compareLabel,
                          )
                        : priorLoading && compareLabel
                        ? `Loading ${compareLabel} comparison...`
                        : null
                    }
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} lg={6}>
                  <ChartCard
                    title="Orders over time"
                    subtitle={`Grouped by ${analytics.granularity}${
                      showComparison ? ` · faded bars are ${compareLabel}` : ""
                    }`}
                  >
                    <BarChart
                      data={chartSeries}
                      margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid stroke={GRID_COLOR} vertical={false} />
                      <XAxis dataKey="label" {...axisProps} minTickGap={16} />
                      <YAxis allowDecimals={false} {...axisProps} width={40} />
                      <Tooltip
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        labelFormatter={withComparisonDate}
                        formatter={(value, name) => [
                          integer.format(value),
                          name,
                        ]}
                      />
                      {showComparison && <Legend />}
                      <Bar
                        dataKey="orderCount"
                        fill={COLOR_ORDERS}
                        name="Orders"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={showComparison ? 18 : 28}
                      />
                      {showComparison && (
                        <Bar
                          dataKey="compareOrderCount"
                          fill={COLOR_ORDERS}
                          fillOpacity={COMPARE_FILL_OPACITY}
                          name={`Orders (${compareLabel})`}
                          radius={[4, 4, 0, 0]}
                          maxBarSize={18}
                        />
                      )}
                    </BarChart>
                  </ChartCard>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <ChartCard
                    title="GMV and NET over time"
                    subtitle={`Both in dollars, on a shared axis${
                      showComparison ? ` · dashed lines are ${compareLabel}` : ""
                    }`}
                  >
                    <LineChart
                      data={chartSeries}
                      margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid stroke={GRID_COLOR} vertical={false} />
                      <XAxis dataKey="label" {...axisProps} minTickGap={16} />
                      <YAxis
                        {...axisProps}
                        width={64}
                        tickFormatter={(value) => currency.format(value)}
                      />
                      <Tooltip
                        labelFormatter={withComparisonDate}
                        formatter={(value, name) => [
                          currencyExact.format(value),
                          name,
                        ]}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="gmv"
                        name="GMV"
                        stroke={COLOR_GMV}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="net"
                        name="NET"
                        stroke={COLOR_NET}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5 }}
                      />
                      {showComparison && (
                        <Line
                          type="monotone"
                          dataKey="compareGmv"
                          name={`GMV (${compareLabel})`}
                          stroke={COLOR_GMV}
                          strokeOpacity={COMPARE_STROKE_OPACITY}
                          strokeDasharray={COMPARE_DASH}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      )}
                      {showComparison && (
                        <Line
                          type="monotone"
                          dataKey="compareNet"
                          name={`NET (${compareLabel})`}
                          stroke={COLOR_NET}
                          strokeOpacity={COMPARE_STROKE_OPACITY}
                          strokeDasharray={COMPARE_DASH}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      )}
                    </LineChart>
                  </ChartCard>
                </Grid>
              </Grid>

              {showComparison && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6">
                      Comparison vs {compareLabel}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {comparisonModeDescription(compareMode)}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Metric</TableCell>
                            <TableCell align="right">Selected period</TableCell>
                            <TableCell align="right">
                              {sentenceCase(compareLabel)}
                            </TableCell>
                            <TableCell align="right">Change</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>Orders</TableCell>
                            <TableCell align="right">
                              {integer.format(analytics.orderCount)}
                            </TableCell>
                            <TableCell align="right">
                              {integer.format(priorAnalytics.orderCount)}
                            </TableCell>
                            <TableCell align="right">
                              {comparisonText(
                                analytics.orderCount,
                                priorAnalytics.orderCount,
                                compareLabel,
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>GMV</TableCell>
                            <TableCell align="right">
                              {currencyExact.format(analytics.gmv)}
                            </TableCell>
                            <TableCell align="right">
                              {currencyExact.format(priorAnalytics.gmv)}
                            </TableCell>
                            <TableCell align="right">
                              {comparisonText(
                                analytics.gmv,
                                priorAnalytics.gmv,
                                compareLabel,
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>NET</TableCell>
                            <TableCell align="right">
                              {currencyExact.format(analytics.net)}
                            </TableCell>
                            <TableCell align="right">
                              {currencyExact.format(priorAnalytics.net)}
                            </TableCell>
                            <TableCell align="right">
                              {comparisonText(
                                analytics.net,
                                priorAnalytics.net,
                                compareLabel,
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Average order value</TableCell>
                            <TableCell align="right">
                              {currencyExact.format(analytics.averageOrderValue)}
                            </TableCell>
                            <TableCell align="right">
                              {currencyExact.format(
                                priorAnalytics.averageOrderValue,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {comparisonText(
                                analytics.averageOrderValue,
                                priorAnalytics.averageOrderValue,
                                compareLabel,
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Breakdown
                  </Typography>
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Period</TableCell>
                          <TableCell align="right">Orders</TableCell>
                          <TableCell align="right">GMV</TableCell>
                          <TableCell align="right">NET</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {series.map((bucket) => (
                          <TableRow key={bucket.bucketStart} hover>
                            <TableCell>{bucket.label}</TableCell>
                            <TableCell align="right">
                              {integer.format(bucket.orderCount)}
                            </TableCell>
                            <TableCell align="right">
                              {currencyExact.format(bucket.gmv)}
                            </TableCell>
                            <TableCell align="right">
                              {currencyExact.format(bucket.net)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Average order value
                      </Typography>
                      <Typography variant="subtitle1">
                        {currencyExact.format(analytics.averageOrderValue)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Commission
                      </Typography>
                      <Typography variant="subtitle1">
                        {currencyExact.format(analytics.commissionTotal)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Service fees
                      </Typography>
                      <Typography variant="subtitle1">
                        {currencyExact.format(analytics.serviceFeeTotal)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Stripe fees (est.)
                      </Typography>
                      <Typography variant="subtitle1">
                        −{currencyExact.format(analytics.stripeFees)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {analytics.refundedAmount > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      {currencyExact.format(analytics.refundedAmount)} was
                      refunded in this period and is already deducted from GMV.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
